package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.colaboradores.DatasColab
import br.com.htms.inspeccaocampo.platform.formatDateTimeBr
import br.com.htms.inspeccaocampo.platform.currentTimeMillis

/**
 * Kit das planilhas de módulo: abas de dados com AutoFiltro + aba Dashboard
 * cujos KPIs, tabelas e gráficos usam SUBTOTAL/SUMPRODUCT e acompanham o filtro.
 */
internal enum class TipoCol { TEXTO, NUMERO, MOEDA, DATA, PCT }

internal data class ColPlan(val titulo: String, val largura: Int = 16, val tipo: TipoCol = TipoCol.TEXTO)

/** Data do Excel (dias desde 30/12/1899). */
internal data class DataXl(val serial: Long)

/** Fórmula por linha; `{r}` vira o número da linha. */
internal data class Fx(val formula: String, val cache: Any? = null)

internal object PlanData {
    private const val OFFSET_EXCEL = 25569L

    fun hojeSerial(): Long = DatasColab.hoje() + OFFSET_EXCEL

    /** Aceita aaaa-mm-dd ou dd/mm/aaaa; null se vazio/inválido. */
    fun de(texto: String?): DataXl? {
        val t = texto?.trim().orEmpty()
        if (t.isEmpty()) return null
        val dias = DatasColab.diasDeIso(t) ?: DatasColab.diasDeBr(t.take(10)) ?: return null
        return DataXl(dias + OFFSET_EXCEL)
    }

    fun deMs(ms: Long?): DataXl? {
        if (ms == null || ms <= 0) return null
        return DataXl((ms - 3 * 3_600_000L).floorDiv(86_400_000L) + OFFSET_EXCEL)
    }

    /** "aaaa-mm" da data (vazio se nula). */
    fun mes(d: DataXl?): String = d?.let { DatasColab.isoDeDias(it.serial - OFFSET_EXCEL).take(7) }.orEmpty()

    fun geradoEm(): String = formatDateTimeBr(currentTimeMillis())
}

/** Aba de dados já posicionada: cabeçalho na linha 3, dados a partir da 4. */
internal class AbaDados(
    val nome: String,
    val colunas: List<ColPlan>,
    val qtdLinhas: Int,
    val sheet: SharedExcelSheet
) {
    val primeira = 4
    val ultima: Int get() = (primeira + qtdLinhas - 1).coerceAtLeast(primeira)
    private val ref = "'${nome.replace("'", "''")}'"

    fun letra(titulo: String): String {
        val i = colunas.indexOfFirst { it.titulo == titulo }
        require(i >= 0) { "Coluna '$titulo' não existe em $nome" }
        return PlanKit.colRef(i)
    }

    fun faixa(titulo: String): String {
        val c = letra(titulo)
        return "$ref!\$$c\$$primeira:\$$c\$$ultima"
    }

    private fun ancora(titulo: String): String {
        val c = letra(titulo)
        return "$ref!\$$c\$$primeira"
    }

    /** Vetor 1/0 das linhas visíveis (para usar dentro de SUMPRODUCT). */
    fun visiveis(titulo: String = colunas.first().titulo): String =
        "SUBTOTAL(103,OFFSET(${ancora(titulo)},ROW(${faixa(titulo)})-ROW(${ancora(titulo)}),0))"

    /** Linhas visíveis (conta a primeira coluna, sempre preenchida). */
    fun contar(): String = "SUBTOTAL(3,${faixa(colunas.first().titulo)})"

    fun somar(titulo: String): String = "SUBTOTAL(9,${faixa(titulo)})"

    fun contarSe(titulo: String, valor: String): String =
        "SUMPRODUCT((${faixa(titulo)}=${PlanKit.texto(valor)})*${visiveis(titulo)})"

    /** Conta linhas visíveis cujo texto começa com [prefixo]. */
    fun contarComeca(titulo: String, prefixo: String): String =
        "SUMPRODUCT((LEFT(${faixa(titulo)},${prefixo.length})=${PlanKit.texto(prefixo)})*${visiveis(titulo)})"

    /** Linhas visíveis com valor maior que zero em [titulo] (coluna só com números ou vazia). */
    fun contarPositivos(titulo: String): String =
        "SUMPRODUCT((${faixa(titulo)}>0)*${visiveis(titulo)})"

    /** [tituloSoma] deve conter só números ou células vazias. */
    fun somarSe(tituloCrit: String, valor: String, tituloSoma: String): String =
        "SUMPRODUCT((${faixa(tituloCrit)}=${PlanKit.texto(valor)})*(${faixa(tituloSoma)})*${visiveis(tituloCrit)})"
}

internal data class KpiPlan(
    val rotulo: String,
    val formula: String,
    val cache: Double,
    val estilo: Int = SharedOoxmlWriter.STYLE_KPI_VALUE
)

/** Bloco do dashboard: tabela (rótulo + valor por fórmula) e gráfico ao lado. */
internal data class BlocoPlan(
    val titulo: String,
    val colRotulo: String,
    val itens: List<Pair<String, Double>>,
    val formula: (String) -> String,
    val grafico: SharedExcelChartType? = SharedExcelChartType.BAR,
    val moeda: Boolean = false,
    /** Total do período para fechar a linha "Outros" quando há mais itens que [limite]. */
    val formulaTotal: String? = null,
    val limite: Int = 12
)

internal object PlanKit {
    private const val S_BODY = SharedOoxmlWriter.STYLE_BODY
    private const val S_HEADER = SharedOoxmlWriter.STYLE_HEADER
    private const val S_TITLE = SharedOoxmlWriter.STYLE_TITLE
    private const val S_KPI_LABEL = SharedOoxmlWriter.STYLE_KPI_LABEL
    private const val S_SECTION = SharedOoxmlWriter.STYLE_SECTION
    private const val S_NOTA = SharedOoxmlWriter.STYLE_NOTA

    fun colRef(col: Int): String {
        var c = col
        val sb = StringBuilder()
        do {
            sb.insert(0, ('A'.code + c % 26).toChar())
            c = c / 26 - 1
        } while (c >= 0)
        return sb.toString()
    }

    fun texto(s: String): String = "\"${s.replace("\"", "\"\"")}\""

    private fun estiloDe(tipo: TipoCol): Int = when (tipo) {
        TipoCol.TEXTO -> S_BODY
        TipoCol.NUMERO -> SharedOoxmlWriter.STYLE_NUMERO
        TipoCol.MOEDA -> SharedOoxmlWriter.STYLE_MOEDA
        TipoCol.DATA -> SharedOoxmlWriter.STYLE_DATA
        TipoCol.PCT -> SharedOoxmlWriter.STYLE_PCT
    }

    private fun celula(col: Int, linha: Int, v: Any?, tipo: TipoCol): SharedExcelCell? {
        val estilo = estiloDe(tipo)
        return when (v) {
            null -> SharedExcelCell(col, "", estilo)
            is DataXl -> SharedExcelCell(col, v.serial.toString(), SharedOoxmlWriter.STYLE_DATA, numeric = true)
            is Fx -> {
                val f = v.formula.replace("{r}", linha.toString())
                when (val c = v.cache) {
                    is Number -> SharedExcelCell(col, c.toDouble().toString(), estilo, numeric = true, formula = f)
                    is DataXl -> SharedExcelCell(col, c.serial.toString(), SharedOoxmlWriter.STYLE_DATA, numeric = true, formula = f)
                    else -> SharedExcelCell(col, c?.toString().orEmpty(), estilo, formula = f)
                }
            }
            is Number -> SharedExcelCell(col, v.toDouble().toString(), estilo, numeric = true)
            is Boolean -> SharedExcelCell(col, if (v) "Sim" else "Não", S_BODY)
            else -> {
                val s = v.toString()
                if (s.isEmpty()) SharedExcelCell(col, "", estilo) else SharedExcelCell(col, s, S_BODY)
            }
        }
    }

    fun abaDados(
        nome: String,
        titulo: String,
        nota: String,
        colunas: List<ColPlan>,
        linhas: List<List<Any?>>,
        cor: String = "1565C0"
    ): AbaDados {
        val rows = mutableListOf<SharedExcelRow>()
        rows += SharedExcelRow(1, listOf(SharedExcelCell(0, titulo, S_TITLE)), height = 24)
        rows += SharedExcelRow(2, listOf(SharedExcelCell(0, nota, S_NOTA)))
        rows += SharedExcelRow(3, colunas.mapIndexed { i, c -> SharedExcelCell(i, c.titulo, S_HEADER) }, height = 30)
        linhas.forEachIndexed { idx, valores ->
            val r = 4 + idx
            rows += SharedExcelRow(r, colunas.indices.mapNotNull { i -> celula(i, r, valores.getOrNull(i), colunas[i].tipo) })
        }
        val ultimaCol = colRef(colunas.size - 1)
        val ultimaLinha = (3 + linhas.size).coerceAtLeast(3)
        val sheet = SharedExcelSheet(
            name = nome,
            rows = rows,
            colWidths = colunas.mapIndexed { i, c -> i to c.largura }.toMap(),
            merges = listOf("A1:${ultimaCol}1", "A2:${ultimaCol}2"),
            freezeRows = 3,
            autoFilter = "A3:$ultimaCol$ultimaLinha",
            tabColor = cor
        )
        return AbaDados(nome, colunas, linhas.size, sheet)
    }

    /**
     * Dashboard: título, KPIs (8 por linha) e blocos empilhados — tabela em A:B
     * e gráfico em D:K. Tudo por fórmula sobre as abas de dados.
     */
    fun dashboard(
        nome: String,
        titulo: String,
        nota: String,
        kpis: List<KpiPlan>,
        blocos: List<BlocoPlan>,
        cor: String = "0D47A1"
    ): SharedExcelSheet {
        val rows = mutableListOf<SharedExcelRow>()
        val charts = mutableListOf<SharedExcelChart>()
        rows += SharedExcelRow(1, listOf(SharedExcelCell(0, titulo, S_TITLE)), height = 26)
        rows += SharedExcelRow(2, listOf(SharedExcelCell(0, nota, S_NOTA)))
        val slots = listOf(0, 1, 3, 4, 5, 6, 7, 8)
        var r = 4
        kpis.chunked(slots.size).forEach { grupo ->
            rows += SharedExcelRow(r, grupo.mapIndexed { i, k -> SharedExcelCell(slots[i], k.rotulo, S_KPI_LABEL) }, height = 30)
            rows += SharedExcelRow(
                r + 1,
                grupo.mapIndexed { i, k -> SharedExcelCell(slots[i], k.cache.toString(), k.estilo, numeric = true, formula = k.formula) },
                height = 28
            )
            r += 3
        }
        blocos.forEach { b ->
            val inicio = r
            rows += SharedExcelRow(r, listOf(SharedExcelCell(0, b.titulo, S_SECTION), SharedExcelCell(1, "", S_SECTION)))
            r++
            rows += SharedExcelRow(r, listOf(SharedExcelCell(0, b.colRotulo, S_HEADER), SharedExcelCell(1, if (b.moeda) "Valor" else "Qtd", S_HEADER)))
            r++
            val principais = b.itens.take(b.limite)
            val estiloValor = if (b.moeda) SharedOoxmlWriter.STYLE_MOEDA else SharedOoxmlWriter.STYLE_NUMERO
            val primeiraDado = r
            if (principais.isEmpty()) {
                rows += SharedExcelRow(r++, listOf(SharedExcelCell(0, "(sem dados)", S_BODY), SharedExcelCell(1, "0", estiloValor, numeric = true)))
            }
            principais.forEach { (rot, v) ->
                rows += SharedExcelRow(
                    r,
                    listOf(
                        SharedExcelCell(0, rot.ifBlank { "(em branco)" }, S_BODY),
                        SharedExcelCell(1, v.toString(), estiloValor, numeric = true, formula = b.formula(rot))
                    )
                )
                r++
            }
            val resto = b.itens.drop(b.limite)
            if (resto.isNotEmpty() && b.formulaTotal != null) {
                rows += SharedExcelRow(
                    r,
                    listOf(
                        SharedExcelCell(0, "Outros (${resto.size})", S_BODY),
                        SharedExcelCell(
                            1, resto.sumOf { it.second }.toString(), estiloValor, numeric = true,
                            formula = "MAX(0,${b.formulaTotal}-SUM(B$primeiraDado:B${r - 1}))"
                        )
                    )
                )
                r++
            }
            val ultimaDado = r - 1
            val altura = 17
            if (b.grafico != null && b.itens.isNotEmpty()) {
                val cats = (primeiraDado..ultimaDado).map { linha -> rows.first { it.index == linha }.cells[0].value }
                val vals = (primeiraDado..ultimaDado).map { linha -> rows.first { it.index == linha }.cells[1].value.toDoubleOrNull() ?: 0.0 }
                charts += SharedExcelChart(
                    type = b.grafico,
                    title = b.titulo,
                    fromCol = 3,
                    fromRow = inicio - 1,
                    toCol = 11,
                    toRow = inicio - 1 + altura,
                    catRange = "\$A\$$primeiraDado:\$A\$$ultimaDado",
                    valRange = "\$B\$$primeiraDado:\$B\$$ultimaDado",
                    categories = cats,
                    values = vals
                )
            }
            r = maxOf(r, inicio + altura) + 2
        }
        return SharedExcelSheet(
            name = nome,
            rows = rows,
            charts = charts,
            colWidths = mapOf(0 to 34, 1 to 20, 2 to 3) + (3..11).associateWith { 20 },
            merges = listOf("A1:L1", "A2:L2"),
            freezeRows = 2,
            tabColor = cor
        )
    }

    fun gerar(abas: List<SharedExcelSheet>): ByteArray = SharedOoxmlWriter.write(abas, emptyMap())

    /** Contagem decrescente por chave (para montar os blocos com valores em cache). */
    fun <T> contagem(itens: List<T>, chave: (T) -> String): List<Pair<String, Double>> =
        itens.groupingBy(chave).eachCount().map { it.key to it.value.toDouble() }.sortedByDescending { it.second }

    fun <T> soma(itens: List<T>, chave: (T) -> String, valor: (T) -> Double): List<Pair<String, Double>> =
        itens.groupBy(chave).map { (k, v) -> k to v.sumOf(valor) }.filter { it.second != 0.0 }.sortedByDescending { it.second }

    fun numero(s: String?): Double? =
        s?.trim()?.replace(".", "")?.replace(',', '.')?.filter { it.isDigit() || it == '.' || it == '-' }?.toDoubleOrNull()
}
